export class ApiError extends Error {
  constructor(message, status, body) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

// Signed-in user's username (session.user.username from /api/auth/session),
// kept in sync by SessionGuard on every session change — mirrors the
// apiFetch._onUnauthorized wiring in AuthProvider. Backend audit_logs rows
// read this from the request's `username` field; falls back to
// AuditLogService::UNKNOWN_USERNAME when null/absent.
apiFetch._username = null;

// Attaches the current username to a mutating request's payload without
// clobbering a caller-supplied one.
function withUsername(fields = {}) {
  return apiFetch._username ? { ...fields, username: apiFetch._username } : fields;
}

export async function apiFetch(path, options = {}) {
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { "Content-Type": "application/json" }),
    ...(options.headers || {}),
  };

  const proxyUrl = `/api/api-proxy?path=${encodeURIComponent(path)}`;

  const res = await fetch(proxyUrl, {
    ...options,
    headers,
  });

  const contentType = res.headers.get("content-type") || "";
  const body = contentType.includes("application/json")
    ? await res.json()
    : await res.text();

  if (!res.ok) {
    if (res.status === 401) {
      apiFetch._onUnauthorized?.();
    }
    throw new ApiError(
      `Request failed: ${res.status} ${res.statusText}`,
      res.status,
      body
    );
  }

  return body;
}

export function fetchReviewDocuments(signal) {
  return apiFetch("/api/documents/review", { signal });
}

export function fetchReviewDocument(id, signal) {
  if (id === "suitable-for-mating-1") {
    const suitable_for_mating_test_data = {
      audit: {
        id: 48,
        pdf_id: 48,
        source_filename: "suitable-for-mating.pdf",
        document_count: 1,
        created_at: "2026-07-07 08:36:02",
        updated_at: "2026-07-07 08:36:02",
      },
      documents: [
        {
          document_id: 314,
          horse_data: {
            id: 314,
            document_id: 314,
            horse_name: "originally parsed name '24",
            dam_name: "original dam",
            yob: "2025",
            eva_result: "Negative",
            collection_date: "10\/30\/2025",
            review_status: "unreviewed",
            additional_notes: "",
            created_at: "2026-07-07 08:36:02",
            updated_at: "2026-07-07 08:36:02",
            sale_name: "Nov Breeding 2025",
            sale_entry_horse_name: "Take Charge Babe",
            sale_entry_dam_name: "Bodebabe",
            sale_entry_foaling_year: "2024-04-25T00:00:00",
            sale_entry_entry_detail_id: 319125,
            sale_entry_hip_number: "231",
            sale_entry_sale_name: "Nov Breeding 2025",
            sale_entry_sale_id: "127",
            sale_entry_sale_date: "2025-11-05T00:00:00",

            // new Coggins fields
            // document_type: "Coggins",
            horse_color: "Gray/Roan",
            horse_age: "1000",
            vet_signature: "Yes",
            ascension_number: "Yes",
            original_or_copy: "Original",

            // new suitable-for-mating fields
            hip_number: "144",
            document_type: "Suitable for Mating",
            pregnancy_status: "Pregnant",
            describe_condition: "Maiden",
            suitable_for_mating: "Yes",
            pregnancy_status_change: "No",
            
          },
          matches: [
            {
              id: 119,
              document_id: 314,
              eva_document_detail_id: 314,
              horse_name: "originally parsed name '24",
              collection_date: "10\/30\/2025",
              review_status: "unreviewed",
              sale_entry_horse_name: "Take Charge Babe",
              hip_number: "231",
              dam_name: "Bodebabe",
              yob: "2021",
              sale_name: "Nov Breeding 2025",
              sale_id: "127",
              sale_date: "2025-11-05T00:00:00",
              entry_detail_id: 324244,
              session_number: 2,
              out_status: "Out",

              // new suitable-for-mating fields
              sale_entry_pregnancy_status: "Not Pregnant",
            },
          ],
        },
      ],
    };
    return Promise.resolve(suitable_for_mating_test_data);
  }
  return apiFetch(`/api/documents/review/${id}`, { signal });
}

export function uploadPdfs(files, { documentType, signal } = {}) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('pdf_files[]', file); // Must match Laravel definition.
  }
  if (documentType) {
    formData.append('document_type', documentType);
  }
  if (apiFetch._username) {
    formData.append('username', apiFetch._username);
  }
  return apiFetch('/api/pdf/upload', {
    method: 'POST',
    body: formData,
    signal,
  });
}

export function uploadPdfsManual(files, { documentType, signal } = {}) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('pdf_files[]', file);
  }
  if (documentType) {
    formData.append('document_type', documentType);
  }
  if (apiFetch._username) {
    formData.append('username', apiFetch._username);
  }
  return apiFetch('/api/pdf/upload/manual', {
    method: 'POST',
    body: formData,
    signal,
  });
}

export function getPdfUrl(documentId) {
  return `/api/api-proxy?path=${encodeURIComponent(`/documents/${documentId}/pdf`)}`;
}

export function fetchQueuedDocuments(signal) {
  return apiFetch('/api/documents/queued', { signal });
}

export function updateEvaDocument(id, fields, signal) {
  return apiFetch(`/api/documents/update/eva/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername(fields)),
    signal,
  });
}

export function updateCogginsDocument(id, fields, signal) {
  return apiFetch(`/api/documents/update/coggins/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername(fields)),
    signal,
  });
}

export function updateSuitableForMatingDocument(id, fields, signal) {
  return apiFetch(`/api/documents/update/suitable-for-mating/${id}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername(fields)),
    signal,
  });
}

export function searchSaleEntries(name, signal) {
  return apiFetch(`/api/sale-entries/search?name=${encodeURIComponent(name)}`, { signal });
}

export function fetchSaleEntryByDocument(documentId, signal) {
  return apiFetch(`/api/sale-entries/by-document/${documentId}`, { signal });
}

export function fetchEvaDocuments(saleId, signal) {
  const path = saleId
    ? `/api/documents/eva/all?sale_id=${encodeURIComponent(saleId)}`
    : '/api/documents/eva/all';
  return apiFetch(path, { signal });
}

export function fetchCogginsDocuments(saleId, signal) {
  const path = saleId
    ? `/api/documents/coggins/all?sale_id=${encodeURIComponent(saleId)}`
    : '/api/documents/coggins/all';
  return apiFetch(path, { signal });
}

export function fetchSuitableForMatingDocuments(saleId, signal) {
  const path = saleId
    ? `/api/documents/suitable-for-mating/all?sale_id=${encodeURIComponent(saleId)}`
    : '/api/documents/suitable-for-mating/all';
  return apiFetch(path, { signal });
}

export function updateSaleEntryMatchStatus(matchId, status, signal) {
  return apiFetch(`/api/sale-entry-matches/${matchId}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername({ review_status: status })),
    signal,
  });
}

// Coggins-origin matches are rejected by /api/sale-entry-matches/{id} (see
// SaleEntryMatchController::updateStatus's guard) — this hits the parallel
// Coggins endpoint instead.
export function updateCogginsMatchStatus(matchId, status, signal) {
  return apiFetch(`/api/coggins-matches/${matchId}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername({ review_status: status })),
    signal,
  });
}

// Also rejected by /api/sale-entry-matches/{id} and /api/coggins-matches/{id}
// (see SuitableForMatingMatchController::updateStatus's two-clause guard) —
// this hits the dedicated Suitable for Mating endpoint instead.
export function updateSuitableForMatingMatchStatus(matchId, status, signal) {
  return apiFetch(`/api/suitable-for-mating-matches/${matchId}`, {
    method: 'PATCH',
    body: JSON.stringify(withUsername({ review_status: status })),
    signal,
  });
}

export function addEvaMatch({ evaDocumentDetailId, entryDetailId, saleName, saleDate, status }, signal) {
  return apiFetch('/api/sale-entry-matches', {
    method: 'POST',
    body: JSON.stringify(withUsername({
      eva_document_detail_id: evaDocumentDetailId,
      entry_detail_id: entryDetailId,
      sale_name: saleName,
      sale_date: saleDate,
      review_status: status,
    })),
    signal,
  });
}

export function addCogginsMatch({ cogginsDocumentDetailId, entryDetailId, saleName, saleDate, status }, signal) {
  return apiFetch('/api/coggins-matches', {
    method: 'POST',
    body: JSON.stringify(withUsername({
      coggins_document_detail_id: cogginsDocumentDetailId,
      entry_detail_id: entryDetailId,
      sale_name: saleName,
      sale_date: saleDate,
      review_status: status,
    })),
    signal,
  });
}

export function addSuitableForMatingMatch({ suitableForMatingDocumentDetailId, entryDetailId, saleName, saleDate, status }, signal) {
  return apiFetch('/api/suitable-for-mating-matches', {
    method: 'POST',
    body: JSON.stringify(withUsername({
      suitable_for_mating_document_detail_id: suitableForMatingDocumentDetailId,
      entry_detail_id: entryDetailId,
      sale_name: saleName,
      sale_date: saleDate,
      review_status: status,
    })),
    signal,
  });
}

// Shared for both types — /api/sale-entry-matches/{id} DELETE isn't
// type-guarded, unlike the PATCH/approve path.
export function removeEvaMatch(matchId, signal) {
  return apiFetch(`/api/sale-entry-matches/${matchId}`, {
    method: 'DELETE',
    signal,
  });
}

export function deleteEvaDocument(documentId, signal) {
  return apiFetch(`/api/documents/${documentId}`, {
    method: 'DELETE',
    signal,
  });
}

export function deleteDocumentAudit(auditId, signal) {
  return apiFetch(`/api/documents/audit/${auditId}`, {
    method: 'DELETE',
    signal,
  });
}

export function addHorseToDocument(auditId, horseData, signal) {
  return apiFetch(`/api/documents/audit/${auditId}/horse`, {
    method: 'POST',
    body: JSON.stringify(withUsername(horseData)),
    signal,
  });
}

// Global pin — visible to every reviewer, unlike the client-only "local"
// pin state (see pinnedLocally.js, no backend call at all).
export function pinDocumentAudit(auditId, signal) {
  return apiFetch(`/api/documents/audit/${auditId}/pin`, {
    method: 'POST',
    body: JSON.stringify(withUsername({})),
    signal,
  });
}

export function unpinDocumentAudit(auditId, signal) {
  return apiFetch(`/api/documents/audit/${auditId}/pin`, {
    method: 'DELETE',
    body: JSON.stringify(withUsername({})),
    signal,
  });
}

export function addCogginsHorseToDocument(auditId, horseData, signal) {
  return apiFetch(`/api/documents/audit/${auditId}/coggins-horse`, {
    method: 'POST',
    body: JSON.stringify(withUsername(horseData)),
    signal,
  });
}

export function addSuitableForMatingHorseToDocument(auditId, horseData, signal) {
  return apiFetch(`/api/documents/audit/${auditId}/suitable-for-mating-horse`, {
    method: 'POST',
    body: JSON.stringify(withUsername(horseData)),
    signal,
  });
}

export function fetchAvailableSales(signal) {
  return apiFetch('/api/available-sales', { signal });
}

export function fetchFailedDocuments(signal) {
  return apiFetch('/api/documents/failed', { signal });
}

export function deleteFailedDocument(id, signal) {
  return apiFetch(`/api/documents/pdf-job/${id}`, {
    method: 'DELETE',
    signal,
  });
}

export function rematchDocuments(signal) {
  return apiFetch('/api/documents/rematch', {
    method: 'POST',
    signal,
  });
}

// Horse-level audit trail (documents.id) — not the whole-upload variant
// (/api/documents/audit/{auditId}/audit-logs), which the frontend doesn't use yet.
export function fetchAuditLogs(documentId, signal) {
  return apiFetch(`/api/documents/${documentId}/audit-logs`, { signal });
}

export function updatePrintedStatus(saleEntryMatchesIds, printedStatus, signal) {
  return apiFetch('/api/sale-entry-matches/printed-status', {
    method: 'PATCH',
    body: JSON.stringify(withUsername({
      sale_entry_match_ids: saleEntryMatchesIds,
      printed_status: printedStatus,
    })),
    signal,
  });
}
